import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Loader2, LockKeyhole } from "lucide-react";
import { useTransferFlow } from "../../context/TransferFlowContext";
import { useMe, useRecipient } from "../../hooks/useMe";
import { ErrorNotice } from "../errors/ErrorNotice";

export function TransactionSummary() {
  const navigate = useNavigate();

  const { transferId, recipientId, quote, setQuote } = useTransferFlow();
  const { recipient } = useRecipient(recipientId);
  const {
    acceptQuoteAsync, isAcceptingQuote,
    confirmTransferAsync, isConfirmingTransfer,
    createQuoteAsync, isCreatingQuote,
  } = useMe();

  const [error, setError] = useState(null);
  const [pin, setPin] = useState("");
  const submitting = isAcceptingQuote || isConfirmingTransfer || isCreatingQuote;

  const handleConfirm = async () => {
    setError(null);
    if (!/^\d{6}$/.test(pin)) {
      setError("Enter your 6-digit transaction PIN.");
      return;
    }
    try {
      // The quote in context is the one create_my_transfer already created
      // AND accepted server-side (transfer comes back as
      // awaiting_user_confirmation). Only if it's expired do we need a
      // genuinely new quote — and only THAT new quote needs an accept call,
      // since it's the only one that hasn't been accepted yet. Re-accepting
      // the original, already-accepted quote is what throws
      // "Quote cannot be accepted" (its status is already "accepted", not
      // "created"/"presented").
      if (!quote || new Date(quote.expires_at) < new Date()) {
        const refreshedQuote = await createQuoteAsync(transferId);
        await acceptQuoteAsync({ transferId, quoteId: refreshedQuote.id });
        setQuote(refreshedQuote);
      }

      await confirmTransferAsync({ transferId, pin });
      setPin("");

      navigate("/transactionconfirmed");
    } catch (err) {
      setError(err);
    }
  };

  // No quote in context — user landed here directly, or context was reset
  if (!transferId || !quote) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6 text-center">
        <p className="text-sm text-slate-500 mb-4">
          No active transfer to review.
        </p>
        <button
          onClick={() => navigate("/home")}
          className="text-sm font-semibold text-[#0A4730] underline underline-offset-2"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center">
      {/* Top Navigation Bar */}
      <div className="w-full max-w-md px-4 py-4 flex items-center justify-between bg-white border-b border-slate-100 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 rounded-full active:bg-slate-100">
          <ArrowLeft className="h-5 w-5 text-slate-600" />
        </button>
        <h1 className="text-[15px] font-bold text-slate-900">Summary</h1>
        <div className="w-9" />
      </div>

      <div className="w-full max-w-md p-4 space-y-6">
        {/* Verification Card */}
        <div className="bg-white rounded-[28px] border border-slate-100 p-6 shadow-sm">
          <p className="text-[12px] font-bold text-slate-400 uppercase tracking-widest mb-4">You are sending</p>
          <div className="text-4xl font-black text-[#0A4730] mb-6 tabular-nums">
            {quote.send_currency} {quote.send_amount}
          </div>

          <div className="space-y-4 pt-4 border-t border-slate-50">
            <SummaryRow
              label="Recipient"
              value={recipient?.nickname || recipient?.full_name || "—"}
            />
            <SummaryRow
              label="Exchange Rate"
              value={`1 ${quote.send_currency} = ${quote.exchange_rate} ${quote.receive_currency}`}
            />
            <div className="flex justify-between items-center bg-slate-50 p-3 rounded-xl mt-4">
              <span className="text-[12px] font-medium text-slate-600">Recipient gets</span>
              <span className="text-[14px] font-bold text-slate-900">
                {quote.receive_currency} {quote.receive_amount}
              </span>
            </div>
          </div>
        </div>

        {/* Security Note */}
        <div className="flex gap-3 items-start p-4 bg-emerald-50 rounded-2xl border border-emerald-100">
          <ShieldCheck className="text-emerald-600 shrink-0 mt-0.5" size={18} />
          <p className="text-[11px] text-emerald-800 leading-relaxed">
            Your transfer is protected by BluPay's secure encryption. Ensure the recipient details are correct before confirming.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 p-4">
          <label className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-3">
            <LockKeyhole size={15} /> Transaction PIN
          </label>
          <input
            type="password"
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            value={pin}
            onChange={(event) => setPin(event.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="••••••"
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-center tracking-[0.6em] text-lg focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5"
            aria-label="6-digit transaction PIN"
          />
          <p className="mt-2 text-[10px] text-slate-400">
            Required to authorize this transfer. Your PIN is never shown to the recipient.
          </p>
        </div>

        <ErrorNotice
          error={error}
          fallback="Something went wrong confirming this transfer. Please try again."
        />

        {/* Confirm Button */}
        <button
          onClick={handleConfirm}
          disabled={submitting || pin.length !== 6}
          className="w-full bg-[#0A4730] text-white py-4 rounded-2xl font-bold text-[15px] active:scale-[0.98] transition-all hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {submitting && <Loader2 size={16} className="animate-spin" />}
          {submitting ? "Confirming…" : "Confirm Transfer"}
        </button>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="flex justify-between items-center">
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className="text-[13px] font-semibold text-slate-900">{value}</span>
    </div>
  );
}
